<template>
  <section :class="$style.section">
    <div class="layout-container">
      <div id="better-decisions" ref="better-decisions" :class="$style.visualSection">
        <div :class="$style.visualContainer">
          <img
            :src="previewBackgroundImage"
            alt=""
            :class="$style.cloudImage"
            data-test="background-image"
            loading="lazy"
          />
          <img
            :src="previewImage"
            :alt="t('myProperties.landing.hero.previewImageAlt')"
            :class="$style.previewImage"
            data-test="preview-image"
            loading="lazy"
            decoding="async"
          />
        </div>

        <div :class="$style.ctaWrapper">
          <h1 :class="$style.title">{{ t('myProperties.landing.hero.visualTitle') }}</h1>
          <HgButton
            size="large"
            :theme="isHg ? 'secondary' : 'primary'"
            :class="$style.cta"
            @click="goToOverview"
          >
            {{ t('myProperties.landing.hero.visualCta') }}
          </HgButton>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { isPlatformHg } from '@/helpers/Platform/Platform';
import { getPropertyLandingImageUrl } from '@/helpers/Url/Url';
import { OVERVIEW_PAGE_PATH } from '@/router/routes/constants';
import { trackingClickEvent } from '@/tracking/tracking';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import { HgButton, HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed, onMounted, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const isHg = isPlatformHg();
const { locale, t } = useI18n();
const router = useRouter();

const previewBackgroundHg = getPropertyLandingImageUrl('hero/preview_background_hg_v2.png');
const previewBackgroundIs24 = getPropertyLandingImageUrl('hero/preview_background_is24_v2.png');

const previewImagesHg: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl('hero/properties_preview_hg_de_new.png'),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl('hero/properties_preview_hg_en_new.png'),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl('hero/properties_preview_hg_fr_new.png'),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl('hero/properties_preview_hg_it_new.png'),
};

const previewImagesIs24: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: getPropertyLandingImageUrl('hero/properties_preview_is24_de_new.png'),
  [HgSupportedLanguage.EN]: getPropertyLandingImageUrl('hero/properties_preview_is24_en_new.png'),
  [HgSupportedLanguage.FR]: getPropertyLandingImageUrl('hero/properties_preview_is24_fr_new.png'),
  [HgSupportedLanguage.IT]: getPropertyLandingImageUrl('hero/properties_preview_is24_it_new.png'),
};

const previewImage = computed(() => {
  const imagesByLocale = isHg ? previewImagesHg : previewImagesIs24;
  const currentLocale = locale.value as HgSupportedLanguage;

  return imagesByLocale[currentLocale] ?? imagesByLocale[HgSupportedLanguage.EN];
});

const previewBackgroundImage = computed(() => (isHg ? previewBackgroundHg : previewBackgroundIs24));

const goToOverview = () => {
  trackingClickEvent(TRACKING_ACTION.GO_TO_MY_REAL_ESTATE, TRACKING_LABEL.PROPERTY_OVERVIEW_TEASER);

  router.push(`${OVERVIEW_PAGE_PATH[locale.value]}`);
};

const componentRef = useTemplateRef('better-decisions');
const route = useRoute();
onMounted(() => {
  if (route.hash === '#better-decisions') {
    componentRef.value?.scrollIntoView({ behavior: 'smooth' });
  }
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.section {
  padding: $space-400 0;
}

.visualSection {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: $space-400;
}

.visualContainer {
  position: relative;
  width: 100%;
  max-width: 900px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.cloudImage {
  width: 140%;
  height: auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) translateY(80px);
  z-index: 0;
  pointer-events: none;
  border-radius: $border-radius-xl;
}

.previewImage {
  width: 100%;
  height: auto;
  position: relative;
  z-index: 1;
}

.title {
  font-weight: 600;
  font-size: 32px;
  line-height: 100%;
  letter-spacing: 0;
  color: $color-black;
  max-width: 800px;
}

.ctaWrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-100;
  justify-content: center;
}

.cta {
  color: $color-blue-60;
}
</style>
